import { getCase } from "../../content/lookups.js";
import { fatigueLimit } from "../../domain/run/fatigueLimit.js";
import { useGame } from "../../app/GameProvider.js";
import { useAutoAdvance } from "../../app/useAutoAdvance.js";
import type { ResolutionEvent } from "../../domain/types/index.js";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { carryDelta } from "./stackCarry.js";
import { HappeningStage } from "./HappeningStage.js";
import { EventCaption } from "./EventCaption.js";
import { displayedInPlayCards } from "./ghostInPlay.js";
import { UsedCards } from "../../components/UsedCards.js";
import { DeckStatus } from "../../components/DeckStatus.js";
import { CaseFoePanel } from "./CaseFoePanel.js";
import { statHelpOverlay } from "./statHelp.js";
import { previousMeterValue } from "../../components/meterWidth.js";
import { useCaseStatusDensity } from "./useCaseStatusDensity.js";
import { lookupCard } from "../../content/index.js";
import { ResultStamp } from "./ResultStamp.js";

function changesFor(event: ResolutionEvent): Partial<Record<"progress" | "deadline" | "fatigue" | "block", string>> {
  switch (event.kind) {
    case "progress":
      return { progress: `+${event.gain}` };
    case "heal":
      return { fatigue: `-${event.amount}` };
    case "load":
      return {
        ...(event.progressDelta ? { progress: String(event.progressDelta) } : {}),
        ...(event.fatigueDealt ? { fatigue: `+${event.fatigueDealt}` } : {}),
      };
    case "deadline":
      return { deadline: `-${event.from - event.to}` };
    case "overdue":
      return { fatigue: "+5" };
    default:
      return {};
  }
}

function ChangedValue({ value, delta }: { value: ReactNode; delta?: string }) {
  if (!delta) return <b>{value}</b>;
  const tone = delta.startsWith("-") ? "is-negative" : "is-positive";
  return (
    <b className={`is-value-change ${tone}`}>
      {value}
      <span className="change-delta">{delta}</span>
    </b>
  );
}

export function CaseResolveScreen() {
  const { state, dispatch } = useGame();
  const { screenRef, compact } = useCaseStatusDensity();
  const [sectionEl, setSectionEl] = useState<HTMLElement | null>(null);
  useAutoAdvance(state, () => dispatch({ type: "ADVANCE_RESOLUTION" }));
  // discard 適用でドメインの場は即座に空になる。飛行の発生源として退場前のカードを保持する。
  const lastInPlayRef = useRef(state.run?.inPlay ?? []);

  const run = state.run;
  const active = run?.activeCase;
  const resolution = state.resolution;
  const event = resolution?.events[resolution.index];
  if (!run || !active || !resolution || !event) return null;

  const spec = getCase(active.caseId);
  if (!spec) return null;
  const changes = changesFor(event);
  const workersJustPlayed = run.inPlay
    .filter((card) => lookupCard(card.cardId)?.type === "worker")
    .map((card) => card.instanceId);
  const onStage = event.kind === "load" || event.kind === "overdue";

  if (run.inPlay.length > 0) lastInPlayRef.current = run.inPlay;
  const displayInPlay = displayedInPlayCards(event.kind, run.inPlay, lastInPlayRef.current);

  // 束の運搬先は実測でCSS変数に渡す（アニメーション自体はCSS transition）。
  useEffect(() => {
    const section = sectionEl;
    if (!section || event.kind !== "discard") return;
    const row = section.querySelector(".used-row");
    const zone = section.querySelector('[data-deck-zone="discard"]');
    if (!row || !zone) return;
    const { x, y } = carryDelta(row.getBoundingClientRect(), zone.getBoundingClientRect());
    section.style.setProperty("--carry-x", `${Math.round(x)}px`);
    section.style.setProperty("--carry-y", `${Math.round(y)}px`);
  }, [event.kind, resolution.index, sectionEl]);

  return (
    <section ref={(el) => { screenRef(el); setSectionEl(el); }} className={`screen screen-case is-resolve${compact ? " is-compact" : ""}`} data-event-kind={event.kind}>
      <CaseFoePanel
        name={spec.name}
        progress={active.progress}
        required={active.required ?? spec.required}
        progressValue={<ChangedValue value={active.progress} delta={changes.progress} />}
        deadline={active.deadline}
        deadlineChanging={Boolean(changes.deadline)}
        fatigue={run.fatigue}
        fatigueMax={fatigueLimit(run.organizations)}
        fatigueValue={<ChangedValue value={run.fatigue} delta={changes.fatigue} />}
        block={run.block}
        investments={active.investments}
        blockValue={<ChangedValue value={run.block} delta={changes.block} />}
        progressFrom={previousMeterValue(active.progress, changes.progress)}
        progressGain={event.kind === "progress" ? event.gain : undefined}
        fatigueFrom={previousMeterValue(run.fatigue, changes.fatigue)}
        onHelp={(topic) => dispatch({ type: "OPEN_OVERLAY", overlay: statHelpOverlay(topic) })}
      />
      <div className="event-caption-slot">
        <EventCaption key={`caption-${resolution.index}`} event={event} />
      </div>
      <section className="hand-zone" aria-label="自社の盤面と手札">
        <div className="hand-stack">
          {/* 捨て札が済んだら実行済みゾーンごと消す（捨て札ステップ中は ghost 表示で残る） */}
          {displayInPlay.length > 0 && <UsedCards cards={displayInPlay} freshOnMountIds={workersJustPlayed} />}
        </div>
      </section>
      {onStage && <HappeningStage key={resolution.index} event={event} />}
      <footer className="case-actions">
        <div className="case-action-row">
          <DeckStatus run={run} discardGain={event.kind === "discard" ? event.amount : undefined} />
        </div>
      </footer>
      <ResultStamp
        kind={event.kind}
        message={event.kind === "gameover" ? event.message : undefined}
        action={
          event.kind === "gameover" ? (
            <button type="button" className="btn btn-primary result-overlay-action" onClick={() => dispatch({ type: "ADVANCE_RESOLUTION" })}>
              結果を見る
            </button>
          ) : undefined
        }
      />
    </section>
  );
}
